import {
    VariableProduct,
    Bom,
    ProductFactory,
    BomFactory,
    ProductMapperParameter,
    BomMapperParameter,
} from "@/v2/models/products.js";
import httpMixin from "@/v2/mixins/http.js";
import mapperMixin from "@/v2/mixins/mapper.js";
import companyMixin from "@/v2/mixins/companies.js";
import categoryMixin from "@/v2/mixins/product-categories.js";
import attributeMixin from "@/v2/mixins/product-attributes.js";
import tierMixin from "@/v2/mixins/tiers.js";
import mediaMixin from "@/v2/mixins/medias.js";
import productGroupMixin from "@/v2/mixins/product-groups.js";
import shippingClassMixin from "@/v2/mixins/shipping_class.js";

export default {
    mixins: [
        httpMixin,
        mapperMixin,
        companyMixin,
        categoryMixin,
        tierMixin,
        mediaMixin,
        attributeMixin,
        productGroupMixin,
        shippingClassMixin,
    ],
    methods: {
        // Get from api (ProductTransformer) and convert to frontend model (called by ProductFactory)
        // left one is for frontend and right one coming from backend BaseProductTransformer
        _productMapper(product) {
            // extract the "custom-{field}" elements from the product object
            const customFields = Object.keys(product)
                .filter((key) => key.startsWith("custom-"))
                .reduce((obj, key) => {
                    obj[key] = product[key];
                    return obj;
                }, {});

            const productAttributes = {
                id: product.id,
                name: product.name,
                parent_name: product.parent_name,
                parent_id: product.parent_id,
                parent: product.parent,
                type: product.type,
                status: product.status,
                description: product.description,
                internal_description: product.internal_description,
                volume: product.volume != null ? Number.parseFloat(product.volume) : null,
                dimensional_sum: product.dimensional_sum != null ? Number.parseFloat(product.dimensional_sum) : null,
                weight: product.weight != null ? Number.parseFloat(product.weight) : null,
                height: product.height != null ? Number.parseFloat(product.height) : null,
                length: product.length != null ? Number.parseFloat(product.length) : null,
                width: product.width != null ? Number.parseFloat(product.width) : null,
                video: product.video,
                variation_shelf: product.variation_shelf,
                packsize: product.packsize,
                min_price: product.min_price ? Number.parseFloat(product.min_price) : null,
                max_price: product.max_price ? Number.parseFloat(product.max_price) : null,
                min_quantity: product.min_quantity != null ? Number.parseFloat(product.min_quantity) : null,
                max_quantity: product.max_quantity != null ? Number.parseFloat(product.max_quantity) : null,
                sku: product.sku,
                allow_backorders: product.allow_backorders,
                shipping_class: product.shipping_class,
                shipping_class_id: product.shipping_class_id,
                original_regular_price:
                    product.original_regular_price != null ? Number.parseFloat(product.original_regular_price) : null,
                regular_price: product.regular_price != null ? Number.parseFloat(product.regular_price) : null,
                sale_price: product.sale_price,
                discount_type: product.discount_type ?? null,
                discount_formula: product.discount_formula ?? null,
                cost: product.cost != null ? Number.parseFloat(product.cost) : null,
                lead_time: product.lead_time,
                variations: product.variations,
                components: product.components,
                per_item_pricing: product.per_item_pricing,
                uom: product.uom,
                warehouseInventories: product.warehouseInventories,
                attribute_options: product.attribute_options,
                product_attributes: product.product_attributes,
                bom: product.bom,
                images: product.images,
                categories: product.categories,
                barcode: product.barcode,
                companies: product.companies,
                tiers: product.tiers,
                date_valid_from: product.date_valid_from ? new Date(product.date_valid_from) : null,
                date_valid_to: product.date_valid_to ? new Date(product.date_valid_to) : null,
                is_accounts_excluded: product.is_accounts_excluded,
                is_tiers_excluded: product.is_tiers_excluded,
                ratio_1: product.ratio_1,
                ratio_2: product.ratio_2,
                manage_stock: product.manage_stock,
                stock_quantity: product.stock_quantity !== null ? Number.parseFloat(product.stock_quantity) : null,
                stock_quantity_overwrite:
                    product.stock_quantity_overwrite != null
                        ? Number.parseFloat(product.stock_quantity_overwrite)
                        : null,
                in_stock: product.in_stock,
                tax_class: product.tax_class,
                tax_included: product.tax_included,
                display_price_include_tax: product.display_price_include_tax,
                tax_exempted: product.tax_exempted,
                default_variation: product.default_variation,
                base_variation: product.base_variation,
                disallow_children_backorders: product.disallow_children_backorders,
                split_group: product.split_group,
                competitor_product: product.competitor_product,
                unit_price: product.unit_price,
                variation_height: product.variation_height,
                variation_weight: product.variation_weight,
                base_variation_product_id: product.base_variation_product_id,
                hide: product.hide,
                ...customFields,
                bo_can_delete: true,
                show_action_menu: true,
                points_earned: product.points_earned,
                points_earned_type: product.points_earned_type,
                variation_visibility: product.variation_visibility,
                catalog_visibility: product.catalog_visibility,
                favorited: product.favorited,
                redeem_with_coupon_only: product.redeem_with_coupon_only,
                product_labels: product.product_labels,
                promotion_id: product.promotion_id,
                is_trade_promotion_managed: product.is_trade_promotion_managed,
                promotion_code: product.promotion_code,
                promotion_version: product.promotion_version,
                promotion_detail_url: product.promotion_detail_url,
            };

            return productAttributes;
        },

        _componentMapper(component) {
            return {
                id: component.id,
                bundle_id: component.bundle_id,
                name: component.name,
                number_of_different_products_allowed: component.number_of_different_products_allowed,
                min_quantity: component.min_quantity,
                max_quantity: component.max_quantity,
                different_sku_free_quantity: component.different_sku_free_quantity,
                discount_on_regular_price: component.discount_on_regular_price,
                discount_percentage: component.discount_percentage,
                discount_amount:
                    component.discount_amount == null ? null : Number.parseFloat(component.discount_amount),
                price: component.price == null ? null : Number.parseFloat(component.price),
                composite_minimum_order: component.composite_minimum_order,
                enable_flexi_combo: component.enable_flexi_combo,
                minimum_different_products_allowed: component.minimum_different_products_allowed,
                maximum_different_products_allowed: component.maximum_different_products_allowed,
                scale_up_ratio: component.scale_up_ratio,
                optional: component.optional,
                sequence: component.sequence,
            };
        },

        _uomMapper(uom) {
            return {
                id: uom.id,
                slug: uom.slug,
                name: uom.name,
                conversion_rate: Number.parseFloat(uom.conversion_rate),
                product_id: uom.product_id,
                hide: uom.hide,
                bo_can_delete: true,
                show_action_menu: true,
            };
        },

        // bom from db (VariationTransformer)
        _bomMapper(bom) {
            return {
                material_id: bom.material_id,
                warehouse_id: bom.warehouse_id,
                quantity: Number.parseFloat(bom.quantity),
                product: bom.product,
                bo_can_delete: true,
                show_action_menu: true,
            };
        },

        // converting product to BOM for BOM pop up to BOM inside the product variation
        parseBom(product) {
            console.log("parseBom-> product", product);

            if (product._type != "bom") {
                const bom = new Bom();
                bom.material_id = product.id;
                bom.product = product;
                bom.bo_can_delete = true;
                bom.show_action_menu = true;

                return bom;
            } else {
                return product;
            }
        },

        parseProduct(product) {
            return ProductFactory.construct(this.createMapperParameter(new ProductMapperParameter()), product);
        },

        parseProducts(products) {
            let parsedProducts = [];

            products.forEach((productData) => {
                const product = this.parseProduct(productData);
                parsedProducts.push(product);
            });

            return parsedProducts;
        },

        getProductWithCategories(id, url, payload, success, failure) {
            return this.getData(
                url + "/" + id + "/" + "category",
                payload,
                (response) => {
                    success(this.parseProduct(response.data));
                },
                failure
            );
        },

        getProduct(id, url, payload, success, failure) {
            return this.getData(
                url + "/" + id,
                payload,
                (response) => {
                    success(this.parseProduct(response.data));
                },
                failure
            );
        },

        getProducts(url, payload, success, failure, showLoadingScreen = true) {
            return this.getData(
                url,
                payload,
                (response) => {
                    success(this.parseProducts(response.data.data), response.data.meta);
                },
                failure,
                showLoadingScreen
            );
        },

        deleteProduct(id, url, payload, success, failure) {
            return this.deleteData(
                `${url}/${id}`,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        deleteProducts(url, payload, success, failure) {
            return this.postBulkData(
                url,
                null,
                null,
                payload,
                (response) => {
                    success(response.data);
                },
                failure
            );
        },

        createProduct(url, payload, success, failure) {
            return this.postData(
                url,
                payload,
                (response) => {
                    success(this.parseProduct(response.data));
                },
                failure
            );
        },

        updateProduct(id, url, payload, success, failure) {
            return this.putData(
                `${url}/${id}`,
                payload,
                (response) => {
                    success(this.parseProduct(response.data));
                },
                failure
            );
        },

        createOrUpdateProduct(id = null, url, payload, success, failure) {
            if (id == null) {
                return this.createProduct(url, payload, success, failure);
            } else {
                return this.updateProduct(id, url, payload, success, failure);
            }
        },

        getProductAllowBackorder(product) {
            return product instanceof VariableProduct
                ? product.getSelectedVariation()?.allow_backorders ?? product.allow_backorders
                : product.allow_backorders;
        },
    },
};
